import { BusinessObjectBase } from './base';
import { activitySchema } from '@schemas/business-objects/activity';
import type { ActivityItem } from './activity-item';
import type { Curriculum } from './curriculum';
import type { Id } from '../base-types';
import type { Resource } from './resource';
import type { TextBox } from './text-box';
import type { User } from './user';

export interface ActivityData {
  readonly id: Id;
  readonly name?: string;
  readonly optional?: boolean;
  readonly sequence?: number;
  readonly curriculumId: Id;
  readonly curriculum?: Curriculum;
  readonly activityItems?: ActivityItem[];
  readonly locked?: boolean;
}

export interface Activity extends Readonly<ActivityData> {}
export class Activity extends BusinessObjectBase {
  static readonly entityName = 'Activity' as const;
  static readonly plural = 'activities';
  static schema = activitySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Activity[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Activity(data));
  }

  static async findOne(filter?: any): Promise<Activity | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Activity(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Activity> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Activity(data);
  }

  static async create(data: Omit<ActivityData, 'id'>): Promise<Activity> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Activity(result);
  }

  static async createMany(
    data: Omit<ActivityData, 'id'>[],
  ): Promise<Activity[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Activity(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ActivityData>,
  ): Promise<Activity> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Activity(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Activity.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
